// Konfiguraatio-specific setup-lens derivation for the kanta/salkku overview
// (docs/spec/concepts.md §2.4 → Konfiguraatio portfolio-scope IA).
//
// Pure reduction over the same per-building signals the single-building Konfiguraatio tab bar
// already reads (`config-status-dimensions.ts` — `TAB_CRITERIA` / `TAB_LABEL`), so the portfolio
// situation cards and a building's own tab bar can never disagree about which questions are still
// open for it. It produces the lens-agnostic overview vocabulary (`lib/overview-lens.ts`) so
// Konfiguraatio renders through the same `PerspectiveOverview` composition every lens uses.
// Fetching / fan-out lives in the paired hook, `useConfigSetupLens.ts`; this module only reduces
// already-fetched data and has no React, fetch, or store dependency of its own.
import type { BuildingCompleteness } from "@/lib/api/agent";
import type { BuildingService } from "@/lib/api/building";
import {
  TAB_CRITERIA,
  type ConfigTabKey,
} from "@/lib/config-status-dimensions";
import type { PerBuildingOverview } from "@/lib/overview-lens";

/** The four situation-card questions, in the same order as the building-scope tab bar
 *  (Rakenne · Signaalit · Kyvykkyydet & odotettu toiminta · Ulkoiset palvelut). */
export const SETUP_QUESTION_ORDER: ConfigTabKey[] = [
  "rakenne",
  "signaalit",
  "kyvykkyydet",
  "palvelut",
];

export type SetupQuestion = ConfigTabKey;

type CompletenessTab = "rakenne" | "signaalit" | "kyvykkyydet";

/**
 * Whether `completeness` shows a gap in any criterion `tab` owns, using the identical
 * criterion→tab mapping the single-building cards use (`TAB_CRITERIA`). A building with no
 * equipment yet is treated as still needing every completeness tab — mirroring the
 * single-building card's "empty" tone ("Aloita lisäämällä talotekniikkaa"), not as vacuously
 * complete. Returns `null` (unknown) when `completeness` itself is `null` — the building's
 * completeness fetch failed and this tab must not be reported as either open or resolved.
 */
function tabIsOpen(
  tab: CompletenessTab,
  completeness: BuildingCompleteness | null,
): boolean | null {
  if (completeness === null) return null;
  if (completeness.total_items === 0) return true;
  const criteria = TAB_CRITERIA[tab];
  return completeness.items.some((item) =>
    item.unmet.some((id) => criteria.includes(id)),
  );
}

/** Whether no external service is active yet. `null` (unknown) when the services fetch failed. */
function palvelutIsOpen(services: BuildingService[] | null): boolean | null {
  if (services === null) return null;
  return !services.some((s) => s.is_active);
}

/**
 * One building's per-question setup state. `completeness` / `services` are `null` exactly when
 * that building's own fetch failed (feed-load isolation, in the paired hook) — those questions
 * are then left out of `openQuestions` (unknown, not fabricated as either a gap or as resolved)
 * and `dataAvailable` goes `false` if every question ended up unknown.
 */
export function deriveBuildingSetup(
  buildingId: string,
  completeness: BuildingCompleteness | null,
  services: BuildingService[] | null,
): PerBuildingOverview {
  const dataAvailable = completeness !== null || services !== null;
  const openQuestions = SETUP_QUESTION_ORDER.filter((question) => {
    const open =
      question === "palvelut"
        ? palvelutIsOpen(services)
        : tabIsOpen(question, completeness);
    return open === true;
  });
  return {
    buildingId,
    openQuestions,
    complete: dataAvailable && openQuestions.length === 0,
    dataAvailable,
  };
}
